// Landing page static data — single source of truth

export const FEATURE_BLOCKS = [
  {
    key: 'dashboard',
    title: 'Unified Command Center',
    desc: 'Monitor schedules, attendance, and workforce activity from one central dashboard — across all your locations.',
    bullets: ['Live attendance visibility', 'Open shift alerts', 'Team task tracking'],
    img: 'https://restaff.b-cdn.net/Screenshots/dashoard.png',
    alt: 'Workforce management dashboard — ReStaff',
  },
  {
    key: 'schedule',
    title: 'Scheduling & Shift Management',
    desc: 'Create staff schedules in minutes with drag-and-drop shift planning, templates, and conflict detection.',
    bullets: ['Drag-and-drop shift builder', 'Copy last week in one click', 'Publish or save as draft'],
    img: 'https://restaff.b-cdn.net/Screenshots/Schedule.png',
    alt: 'Employee scheduling software — ReStaff',
  },
  {
    key: 'timeclock',
    title: 'Time Tracking & Clock-In',
    desc: 'Employees clock in and out from any device. Managers get precise, payroll-ready timesheets automatically.',
    bullets: ['PIN-based kiosk & mobile clock-in', 'Real-time attendance updates', 'Payroll-ready exports'],
    img: 'https://restaff.b-cdn.net/Screenshots/Time%20Clock.png',
    alt: 'Time clock software for teams — ReStaff',
  },
  {
    key: 'staff',
    title: 'Staff & Role Management',
    desc: 'Manage employees, roles, permissions, and multi-location assignments from a single system.',
    bullets: ['Owner / Manager / Staff roles', 'Multi-location assignment', 'Fast onboarding'],
    img: 'https://restaff.b-cdn.net/Screenshots/Staff.png',
    alt: 'Staff management for multi-location businesses — ReStaff',
  },
  {
    key: 'insights',
    title: 'Labor Cost Insights',
    desc: 'Understand where your money goes. Track budget vs. actual spend and spot overtime risks before they happen.',
    bullets: ['Budget vs. actual tracking', 'Overtime risk alerts', 'Multi-location comparison'],
    img: 'https://restaff.b-cdn.net/Screenshots/Executive%20Insights.png',
    alt: 'Labor cost insights and workforce analytics — ReStaff',
  },
  {
    key: 'reports',
    title: 'Timesheets & Payroll Reports',
    desc: 'Generate workforce reports with hours, costs, and summaries — ready to export for your payroll provider.',
    bullets: ['Hours & cost breakdowns', 'CSV export ready', 'Weekly payroll summaries'],
    img: 'https://restaff.b-cdn.net/Screenshots/Reports.png',
    alt: 'Workforce reporting and payroll exports — ReStaff',
  },
  {
    key: 'documents',
    title: 'Team Communication & Documents',
    desc: 'Upload HR documents, broadcast announcements, and assign tasks — keep your entire team informed and accountable.',
    bullets: ['Centralized HR documents', 'Team announcements', 'Task assignment & tracking'],
    img: 'https://restaff.b-cdn.net/Screenshots/Documents.png',
    alt: 'Document management and team communication — ReStaff',
  },
  {
    key: 'smtp',
    title: 'Smart Email Notifications',
    desc: 'Send shift updates, announcements, and payslips automatically using your own Resend account — no extra email costs.',
    bullets: ['Automated shift notifications', 'Company-wide announcements', 'Professional branded payslips'],
    img: 'https://restaff.b-cdn.net/Screenshots/dashoard.png',
    alt: 'Automated staff email notifications with Resend — ReStaff',
  },
];

export const ALL_PLAN_FEATURES = [
  'Dashboard',
  'Employee Management',
  'Employee Time Clock',
  'Smart Employee Scheduling',
  'Shift Swaps',
  'Task Management',
  'Team Announcements',
  'Notifications',
  'Company Documents',
  'ChatGPT MCP Access',
  'Claude MCP Access',
  'AI Workforce Queries',
  'Secure Tenant AI Access',
  'Automatic Break Deduction',
  'Overtime Calculation (1.25x+)',
  'Holiday Pay Rules',
  'Payroll Generation from Timesheets',
  'PDF Payslips + Email Delivery',
  'Resend Failed Payslip Emails',
  'Cash Advance Deduction Tracking',
  'CSV Payroll Export',
  'PDF Payroll Summary Report',
  'Labor Cost Dashboard',
  'Overtime Detection',
  'Eva AI Assistant',
  'Smart Email Notifications',
  'Billing & Subscription Control',
  'Mobile Clock-In',
  'App Installation',
  'Feature Requests',
  'Support Access',
  'Settings & Permissions',
];

export const DEFAULT_PLANS = [
  {
    key: 'single',
    name: 'Single Location',
    monthlyPrice: 59,
    yearlyMonthly: 29,
    yearlyTotal: 348,
    badge: null,
    desc: 'Everything you need to schedule, track hours, and run payroll — in one place.',
    features: ALL_PLAN_FEATURES,
    cta: 'Start Free Trial',
  },
  {
    key: 'multi',
    name: 'Multi Location',
    monthlyPrice: 119,
    yearlyMonthly: 59,
    yearlyTotal: 708,
    badge: 'Best Value',
    desc: 'Everything in Single Location, plus multi-location payroll and centralized reporting.',
    features: [...ALL_PLAN_FEATURES, 'Multi-Location Management', 'Centralized Payroll Reporting'],
    cta: 'Start Free Trial',
  },
];

export const FAQ_ITEMS = [
  {
    q: 'What is included in the 30-day free trial?',
    a: 'Full access to every feature — scheduling, time clock, staff management, executive insights, documents, and more. Your trial starts the moment you create your account. Cancel anytime, no questions asked.',
  },
  {
    q: 'Can I manage multiple locations under one account?',
    a: 'Yes. The Multi-Location plan supports unlimited locations, each with its own staff, schedules, tasks, and reports — all managed from a single centralized dashboard. Switch between locations instantly.',
  },
  {
    q: 'How does the time clock work?',
    a: 'Employees clock in and out using a secure 4-digit PIN or QR code from any device — no app install needed. You can also set up a shared kiosk terminal using the Mobile Quick Clock feature. Managers review, approve, or edit timesheets directly in the app.',
  },
  {
    q: 'What roles and permissions are available?',
    a: 'ReStaff supports three roles: Owner (full control over all locations and settings), Manager (scoped access to assigned locations), and Staff (personal schedule, time clock, and tasks only). Permissions are granular and enforced at every level.',
  },
  {
    q: 'Does my team need to download an app?',
    a: 'No. ReStaff is a Progressive Web App (PWA) that installs directly on iOS, Android, and desktop — no App Store or Google Play required. It runs like a native app with offline-ready capabilities.',
  },
  {
    q: 'How do shift swaps work?',
    a: 'Staff can request shift swaps directly inside the app. Managers receive an instant notification and can approve or decline in one click. Open swap requests are visible to eligible staff, ensuring coverage gaps are always filled.',
  },
  {
    q: 'Can I export schedules and payroll reports?',
    a: 'Yes. Schedules can be exported as PDF or PNG with your branding. Labor reports, hours summaries, and timesheet data can be exported as CSV — ready for direct use with your payroll provider.',
  },
  {
    q: 'What happens when my trial ends?',
    a: 'After 30 days, you will be prompted to select a plan and enter payment details. Your data is always preserved — nothing is deleted during the transition. You can cancel at any time with no questions asked.',
  },
  {
    q: 'How is billing handled? Is it secure?',
    a: 'All payments are processed through Stripe — the same infrastructure trusted by millions of businesses worldwide. You can upgrade, downgrade, or cancel your subscription at any time from your billing dashboard.',
  },
  {
    q: 'Is there real customer support?',
    a: 'Yes. You can reach our support team directly from inside your ReStaff dashboard. Real people — no chatbots, no automated queues. We also maintain a public status page so you always know the state of the platform.',
  },
];

export const CUSTOMER_LOGOS = [
  { src: 'https://restaff.b-cdn.net/Customers/Agriturismo.png',    alt: 'Agriturismo' },
  { src: 'https://restaff.b-cdn.net/Customers/ama.webp',           alt: 'Ama' },
  { src: 'https://restaff.b-cdn.net/Customers/flo.png',            alt: 'Flo' },
  { src: 'https://restaff.b-cdn.net/Customers/fradis.png',         alt: 'Fradis' },
  { src: 'https://restaff.b-cdn.net/Customers/hungrysushi.png',    alt: 'Hungry Sushi' },
  { src: 'https://restaff.b-cdn.net/Customers/kencho.jpg',         alt: 'Kencho' },
  { src: 'https://restaff.b-cdn.net/Customers/mamamiapizza.png',   alt: 'Mamma Mia Pizza' },
  { src: 'https://restaff.b-cdn.net/Customers/maplewood_cafe.png', alt: 'Maplewood Cafe' },
  { src: 'https://restaff.b-cdn.net/Customers/merald.avif',        alt: 'Merald' },
  { src: 'https://restaff.b-cdn.net/Customers/oncarlo.png',        alt: 'On Carlo' },
  { src: 'https://restaff.b-cdn.net/Customers/Piccolo.jpg',        alt: 'Piccolo' },
  { src: 'https://restaff.b-cdn.net/Customers/redchamber.png',     alt: 'Red Chamber' },
  { src: 'https://restaff.b-cdn.net/Customers/sarda.png',          alt: 'Sarda' },
  { src: 'https://restaff.b-cdn.net/Customers/wei.png',            alt: 'Wei' },
  { src: 'https://restaff.b-cdn.net/Customers/Xiao.jpg',           alt: 'Xiao' },
  { src: 'https://restaff.b-cdn.net/Customers/yun.png',            alt: 'Yun' },
];

export const OTHER_FEATURES = [
  {
    iconKey: 'megaphone',
    title: 'Team Announcements',
    desc: 'Post updates and important messages to your entire team or specific locations instantly.',
    bullets: ['Broadcast to all staff', 'Location-specific posts', 'Pinned announcements'],
  },
  {
    iconKey: 'clipboard',
    title: 'Task Management',
    desc: 'Create, assign, and track operational tasks with deadlines across every location.',
    bullets: ['Assign by role or person', 'Track completion status', 'Daily task summaries'],
  },
  {
    iconKey: 'arrows',
    title: 'Shift Swap Requests',
    desc: 'Staff can propose and accept shift trades. Managers approve in one click.',
    bullets: ['Employee-initiated swaps', 'Manager approval flow', 'Swap history log'],
  },
  {
    iconKey: 'bell',
    title: 'Push Notifications',
    desc: 'Keep your team in sync with real-time alerts for clock-ins, swaps, and schedule changes.',
    bullets: ['Instant schedule alerts', 'Swap & task reminders', 'Works on all devices'],
  },
  {
    iconKey: 'coffee',
    title: 'Break Time Tracking',
    desc: 'Log and monitor paid and unpaid breaks during shifts for accurate labor records.',
    bullets: ['Auto break detection', 'Paid vs unpaid logging', 'Included in timesheet export'],
  },
  {
    iconKey: 'calendar',
    title: 'Availability Management',
    desc: 'Staff set their preferred working hours so managers build schedules that actually fit.',
    bullets: ['Staff submit availability', 'Recurring weekly patterns', 'Conflict alerts on scheduling'],
  },
];
